/* eslint-disable react-hooks/exhaustive-deps */
import { I18n } from '@aws-amplify/core';
import { Button, Form } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { ActionPlanForm } from '@/components/ActionPlan/Form';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useCreateActionPlan } from '@/hooks/v2/useCreateActionPlan';
import { GetActionPlanResponse, useGetActionPlan } from '@/hooks/v2/useGetActionPlan';
import { useGetActionPlanTasks } from '@/hooks/v2/useGetActionPlanTasks';
import { useUpdateActionPlan } from '@/hooks/v2/useUpdateActionPlan';
import { useGetListAttachment } from '../UploadFile/hooks/useGetListAttachment';
import { CreateActionPlanPayload, FormDataValues, PriorityType, Task, TaskType } from './../types';

import * as S from './styles';

interface ActionPlanModalProps {
	onCreateOrUpdate?: () => void;
	open?: boolean;
	title?: string;
	risk: string | JSX.Element;
	onClose: () => void;
	action_plan_id?: string | null;
	table_origin: string;
	table_origin_id: string;
	file_id: string;
	workstation_id: string;
	origin_name: string;
	setActionPlanId: (action_plan_id: string) => void;
	disabled?: boolean;
	resetForm?: boolean;
}

export interface ActionPlanResponse {
	id: string;
	title: string;
	due_date: Date;
}

const { useForm } = Form;

export function ActionPlanModal({
	open = false,
	title = '',
	risk,
	onClose,
	action_plan_id = null,
	table_origin,
	table_origin_id,
	file_id,
	workstation_id,
	origin_name,
	setActionPlanId,
	disabled = false,
	onCreateOrUpdate = () => {},
	resetForm = false
}: Readonly<ActionPlanModalProps>): JSX.Element {
	const [form] = useForm<FormDataValues>();

	const [actionPlanTasks, setActionPlanTasks] = useState<Task[]>([]);
	const [actionPlanResponse, setActionPlanResponse] = useState<GetActionPlanResponse | null>(null);

	const { organization, company } = useApplicationContext();
	const { mutateAsync: createActionPlan } = useCreateActionPlan();
	const { mutateAsync: updateActionPlan } = useUpdateActionPlan();

	const {
		isLoading: isLoadingAttachments,
		data: attachments = [],
		isSuccess
	} = useGetListAttachment({
		organization_id: organization?.id!,
		company_id: company?.id!,
		table_origin: table_origin,
		table_origin_id: table_origin_id!
	});

	const actionPlanParams = {
		id: action_plan_id ?? '',
		organization_id: organization?.id ?? '',
		company_id: company?.id ?? ''
	};

	const { data: actionPlanData } = useGetActionPlan(actionPlanParams);

	const { data: actionPlanTasksData } = useGetActionPlanTasks({
		organization_id: organization?.id ?? '',
		company_id: company?.id ?? '',
		action_plan_id: action_plan_id ?? ''
	});

	useEffect(() => {
		if (actionPlanData) {
			setActionPlanResponse({
				id: actionPlanData.id,
				title: actionPlanData.title,
				due_date: actionPlanData.due_date,
				status: actionPlanData.status,
				score: actionPlanData.score,
				priority: actionPlanData.priority,
				investment_range: actionPlanData.investment_range,
				investment_value: actionPlanData.investment_value,
				description: actionPlanData.description,
				lexo_rank: actionPlanData.lexo_rank,
				file_id: actionPlanData.file_id,
				activity_id: actionPlanData.activity_id,
				workstation_id: actionPlanData.workstation_id,
				user_id: actionPlanData.user_id,
				responsible_user_id: actionPlanData.responsible_user_id,
				action_plan_origin_id: actionPlanData.action_plan_origin_id,
				completed_at: actionPlanData.completed_at,
				created_at: actionPlanData.created_at,
				updated_at: actionPlanData.updated_at,
				deleted_at: actionPlanData.deleted_at,
				author: actionPlanData.author,
				organization: actionPlanData.organization,
				company: actionPlanData.company,
				sector: actionPlanData.sector,
				line: actionPlanData.line,
				origin: actionPlanData.origin,
				workstation: actionPlanData.workstation
			});
			return;
		}

		setActionPlanResponse(null);
	}, [actionPlanData]);

	useEffect(() => {
		if (actionPlanTasksData && actionPlanTasksData.data) {
			setActionPlanTasks(
				actionPlanTasksData.data.map((task) => ({
					id: task.id,
					title: task.title,
					due_date: task.due_date ? moment(task.due_date) : undefined,
					responsible_user_id: task.responsible_user_id,
					is_completed: task.is_completed,
					type: task.type as TaskType
				}))
			);
		}
	}, [actionPlanTasksData]);

	useEffect(() => {
		if (attachments && isSuccess) {
			form.setFieldsValue({ attachments: attachments });
		}
	}, [attachments, isSuccess]);

	useEffect(() => {
		if (open && title) {
			form.setFieldsValue({ title });
		}
	}, [open, title]);

	useEffect(() => {
		if (!actionPlanResponse) {
			form.resetFields();
			form.setFieldsValue({ title });
			return;
		}
		form.setFieldsValue({
			title: actionPlanResponse?.title,
			responsible: actionPlanResponse?.responsible_user_id,
			due_date: moment(actionPlanResponse?.due_date),
			investment_range: actionPlanResponse?.investment_range,
			investment_value: actionPlanResponse?.investment_value || undefined,
			priority: actionPlanResponse?.priority as PriorityType,
			description: actionPlanResponse?.description || undefined
		});
	}, [actionPlanResponse]);

	function mountPayload(values: FormDataValues): CreateActionPlanPayload {
		return {
			organization_id: organization?.id!,
			company_id: company?.id!,
			workstation_id: workstation_id!,
			title: values.title,
			responsible_user_id: values.responsible,
			due_date: moment(values.due_date).format(),
			priority: values.priority,
			investment_range: values.investment_range,
			investment_value: values.investment_value,
			description: values.description,
			tasks: values.task_list?.map((task) => ({
				title: task.title,
				due_date: task.due_date ? moment(task.due_date).format() : undefined,
				responsible_user_id: task.responsible_user_id ? task.responsible_user_id : undefined,
				is_completed: task.is_completed,
				type: task.type || undefined
			})),
			attachments: values.attachments.map((attachment) => attachment.id),
			attachments_temporary: true,
			origin: {
				origin_name: origin_name,
				table_name: table_origin,
				column_id: table_origin_id!,
				file_id: file_id!
			}
		};
	}

	function mountPayloadUpdate(values: FormDataValues): CreateActionPlanPayload {
		return {
			id: action_plan_id!,
			organization_id: organization?.id!,
			company_id: company?.id!,
			workstation_id: workstation_id!,
			title: values.title,
			responsible_user_id: values.responsible,
			due_date: moment(values.due_date).format(),
			investment_range: values.investment_range,
			investment_value: values.investment_value,
			priority: values.priority,
			description: values.description
		};
	}

	async function handleOnSubmit(): Promise<void> {
		const values = await form.validateFields();
		const payload = action_plan_id ? mountPayloadUpdate(values) : mountPayload(values);
		if (action_plan_id) {
			const { data } = await updateActionPlan(payload);
			setActionPlanId(data.id);
		} else {
			const { data } = await createActionPlan(payload);
			setActionPlanId(data.id);
		}
		onClose();
		onCreateOrUpdate();
		if (resetForm) {
			form.resetFields();
			setActionPlanTasks([]);
			setActionPlanResponse(null);
		}
	}

	useEffect(() => {
		if (actionPlanTasks) {
			form.setFieldsValue({
				task_list: actionPlanTasks
			});
		}
	}, [actionPlanTasks]);

	if (isLoadingAttachments) {
		return <></>;
	}

	const initialValues = {
		task_list: actionPlanTasks,
		attachments: attachments
	};

	function getTitle(): string {
		return action_plan_id ? I18n.get('Update action plan') : I18n.get('Create action plan');
	}

	function getFooter(): JSX.Element {
		if (disabled) {
			return <></>;
		}

		return (
			<>
				<Button onClick={onClose}>{I18n.get('Cancel')}</Button>
				<Button type="primary" onClick={handleOnSubmit}>
					{I18n.get('Save')}
				</Button>
			</>
		);
	}

	function handleOnCancel(): void {
		onClose();
		if (resetForm) {
			form.resetFields();
			setActionPlanTasks([]);
			setActionPlanResponse(null);
		}
	}

	return (
		<S.ModalWrapper
			title={getTitle()}
			open={open}
			onCancel={handleOnCancel}
			footer={getFooter()}
			centered
			getContainer={() => document.body}
			width="min(840px, calc(100vw - 32px))"
			style={{ paddingTop: 50, paddingBottom: 50 }}
		>
			<ActionPlanForm
				form={form}
				initialValues={initialValues}
				risk={risk}
				modal={true}
				origin_table={table_origin}
				table_origin_id={table_origin_id}
				actionPlanId={action_plan_id!}
			/>
		</S.ModalWrapper>
	);
}
